import { useMsal } from '@azure/msal-react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useState } from 'react'
import { useTheme } from 'styled-components'

import { useStore } from '../../../application/store/store'
import {
  CGU_URL,
  DASHBOARD_URL,
  DOCUMENTATION_API,
  DOCUMENTS_URL,
  DOWNLOAD_URL,
  MY_PLANTS_URL,
  USER_DOCUMENTATION,
} from '../../../constants/navigation.constants'
import { UserElementAccess } from '../../../domain/user.types'
import logo from '../../../infrastructure/assets/logo_white_v2.png'
import { useCustomTranslation } from '../../../infrastructure/translation/useCustomTranslation'
import { MobilePlantSearchSelected } from '../../components/plantSearchRedirect/mobilePlantSearchSelected'
import PlantSearchRedirect from '../../components/plantSearchRedirect/plantSearchRedirect'
import {
  HelperSpan,
  IconMenu,
  LinksWrapper,
  Logo,
  LogoSearch,
  LogoutButton,
  MenuButton,
  Navbar,
  NavbarTop,
  NavHeader,
  ResponsiveHeader,
  SearchBar,
  StyledExternalLink,
  StyledLink,
  TitleMenu,
  UserName,
  UserWrapper,
} from './navigation.styles'
import environnement from '../../../infrastructure/config/environnement'
import { AUTH_TYPE } from '../../../infrastructure/enums/authentication.enum'

export const Navigation = (): React.ReactElement => {
  const { user, activeMenu, setActiveMenu } = useStore()
  const { t } = useCustomTranslation('common')
  const currentUserAccess: UserElementAccess | undefined = user?.access

  const { instance } = useMsal()
  const { isMobile } = useTheme()
  const [isShown, setIsShown] = useState(!isMobile)

  const handleLogout = async () => {
    localStorage.setItem('isLoggingOut', 'true')
    const selected = localStorage.getItem('selectedType')
    localStorage.removeItem('selectedType')
    localStorage.removeItem('token')

    if (selected === AUTH_TYPE.EXTERNAL) {
      window.location.reload()
      await instance.logoutRedirect({
        postLogoutRedirectUri: `${environnement.FRONT_REDIRECT_URI}`,
        authority: `https://${environnement.EXTERNAL_DOMAIN}.b2clogin.com/${environnement.EXTERNAL_DOMAIN}.onmicrosoft.com/${environnement.EXTERNAL_SIGNIN_FLOW}/oauth2/v2.0/logout`,
      })
    } else {
      window.location.reload()
      await instance.logoutRedirect()
    }
  }

  return (
    <>
      {isMobile && (
        <ResponsiveHeader>
          <MenuButton type="button" onClick={() => setIsShown(!isShown)}>
            <FontAwesomeIcon icon="bars" />
          </MenuButton>
          <MobilePlantSearchSelected />
          <HelperSpan />
        </ResponsiveHeader>
      )}
      <Navbar isShown={isShown}>
        {isMobile && (
          <NavHeader>
            <MenuButton
              style={{ position: 'absolute' }}
              type="button"
              onClick={() => setIsShown(false)}
            >
              <FontAwesomeIcon icon="chevron-left" />
            </MenuButton>
            <LogoSearch>
              <Logo>
                <img src={logo} alt="Urbasolar logo white" />
              </Logo>
              <SearchBar onClick={() => setIsShown(false)}>
                <PlantSearchRedirect />
              </SearchBar>
            </LogoSearch>
          </NavHeader>
        )}
        <NavbarTop>
          {!isMobile && (
            <Logo>
              <img src={logo} alt="Urbasolar logo white" />
            </Logo>
          )}
          <LinksWrapper>
            <StyledLink
              to={DASHBOARD_URL}
              $active={activeMenu === DASHBOARD_URL}
              $isAccessGranted
              onClick={() => {
                if (isMobile) setIsShown(false)
                setActiveMenu(DASHBOARD_URL)
              }}
            >
              <IconMenu>
                <FontAwesomeIcon icon="home" />
              </IconMenu>
              <TitleMenu>{t('dashboard')}</TitleMenu>
            </StyledLink>
            <StyledLink
              to={DOCUMENTS_URL}
              $active={activeMenu === DOCUMENTS_URL}
              $isAccessGranted={Boolean(currentUserAccess?.documentPage)}
              onClick={() => {
                if (isMobile) setIsShown(false)
                setActiveMenu(DOCUMENTS_URL)
              }}
            >
              <IconMenu>
                <FontAwesomeIcon icon="file-alt" />
              </IconMenu>
              <TitleMenu>{t('documents')}</TitleMenu>
            </StyledLink>
            <StyledLink
              to={MY_PLANTS_URL}
              $active={activeMenu === MY_PLANTS_URL}
              $isAccessGranted
              onClick={() => {
                if (isMobile) setIsShown(false)
                setActiveMenu(MY_PLANTS_URL)
              }}
            >
              <IconMenu>
                <FontAwesomeIcon icon="sliders-h" />
              </IconMenu>
              <TitleMenu>{t('my_plants')}</TitleMenu>
            </StyledLink>

            <StyledLink
              to={DOWNLOAD_URL}
              $active={activeMenu === DOWNLOAD_URL}
              $isAccessGranted={Boolean(currentUserAccess?.downloadPage)}
              onClick={() => {
                if (isMobile) setIsShown(false)
                setActiveMenu(DOWNLOAD_URL)
              }}
            >
              <IconMenu>
                <FontAwesomeIcon icon="file-arrow-down" />
              </IconMenu>
              <TitleMenu>{t('downloads')}</TitleMenu>
            </StyledLink>

            <StyledLink
              to={DOCUMENTATION_API}
              $active={activeMenu === DOCUMENTATION_API}
              $isAccessGranted={Boolean(currentUserAccess?.apiDocPage)}
              onClick={() => {
                if (isMobile) setIsShown(false)
                setActiveMenu(DOCUMENTATION_API)
              }}
            >
              <IconMenu>
                <FontAwesomeIcon icon="cog" />
              </IconMenu>
              <TitleMenu>{t('api_doc')}</TitleMenu>
            </StyledLink>

            <StyledLink
              to={CGU_URL}
              $active={activeMenu === CGU_URL}
              $isAccessGranted={true}
              onClick={() => {
                if (isMobile) setIsShown(false)
                setActiveMenu(CGU_URL)
              }}
            >
              <IconMenu>
                <FontAwesomeIcon icon="archive" />
              </IconMenu>
              <TitleMenu>{t('terms_of_service', 'uppercase')}</TitleMenu>
            </StyledLink>
            <StyledExternalLink href={USER_DOCUMENTATION} target="_blank">
              <IconMenu>
                <FontAwesomeIcon icon="circle-question" />
              </IconMenu>
              <TitleMenu>{t('user_help')}</TitleMenu>
            </StyledExternalLink>
          </LinksWrapper>
        </NavbarTop>

        <UserWrapper>
          {user && (
            <>
              <UserName title={user.email}>{user.email}</UserName>
              <LogoutButton type="button" onClick={handleLogout}>
                <FontAwesomeIcon icon="sign-out-alt" />
              </LogoutButton>
            </>
          )}
        </UserWrapper>
      </Navbar>
    </>
  )
}
